import { Metamodel } from 'app/model/base/model';
import { NotebookActivity } from 'app/model/pipeline/notebook.activity';
import { SparkJarActivity } from 'app/model/pipeline/spark-jar.activity';
import { SparkPythonActivity } from 'app/model/pipeline/spark-python.activity';
import { JobActivity } from 'app/model/pipeline/job.activity';
import { SparkActivity } from 'app/model/pipeline/spark.activity';
import { HiveActivity } from 'app/model/pipeline/hive.activity';
import { MapReduceActivity } from 'app/model/pipeline/map-reduce.activity';
import { PigActivity } from 'app/model/pipeline/pig.activity';
import { StreamingActivity } from 'app/model/pipeline/streaming.activity';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { DeleteActivity } from 'app/model/pipeline/delete.activity';
import { GetMetadataActivity } from 'app/model/pipeline/get-metadata.activity';
import { LookupActivity } from 'app/model/pipeline/lookup.activity';
import { IActivityRun } from 'app/model/monitoring/monitoring-models';

import { stringsAreEqualCaseInsensitive } from './string.util';
import { ExecuteDataFlowActivity } from 'app/model/pipeline/execute-dataflow.activity';
import { AzureMLExecutePipelineActivity } from 'app/model/pipeline/azure-ml-execute-pipeline.activity';
import { ExecuteWranglingDataflowActivity } from 'app/model/pipeline/execute-wrangling-dataflow.activity';
import { ExecuteSSISPackageActivity } from 'app/model/pipeline/ssis.activity';
import { IActivityConfiguration } from 'app/authoring/interfaces/IActivityComponent';
import { ContainerActivityConfiguration } from 'app/authoring/component/activities/container-activity-configuration';
import { SwitchActivityConfiguration } from 'app/authoring/component/activities/switch-activity/switch-activity-configuration';
import { IfConditionActivityConfiguration } from 'app/authoring/component/activities/if-condition-activity/if-condition-activity-configuration';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { Activity } from 'app/model/pipeline/activity';
import { SwitchActivity } from 'app/model/pipeline/switch.activity';
import { IfConditionActivity } from 'app/model/pipeline/if-condition.activity';
import { ForEachActivity } from 'app/model/pipeline/foreach.activity';
import { UntilActivity } from 'app/model/pipeline/until.activity';
import { ValidationActivityConfiguration } from 'app/authoring/component/activities/validation-activity/validation-activity-configuration';
import { ExpressionCategory, FabricApprovalActivity, InvokeCopyJobActivity, InvokeDataBuildToolJobActivity, TridentNotebookActivity, TridentSparkJobDefinitionActivity } from 'shell-models-lib';
import { ActivityRegistryService } from 'app/authoring/registries/activity-registry.service';
import { AzureFunctionActivity } from 'app/model/pipeline/azure-function.activity';
import { ExecutePipelineActivityDuplicate } from 'app/model/pipeline/execute-pipeline.activity';

export type ActivityType = 'Copy' | 'Lookup' | 'GetMetadata' | 'Delete';

export function isDatabricksActivity(activityType: string): boolean {
    return activityType === Metamodel.modelFor(NotebookActivity).name
        || activityType === Metamodel.modelFor(SparkJarActivity).name
        || activityType === Metamodel.modelFor(SparkPythonActivity).name
        || activityType === Metamodel.modelFor(JobActivity).name;
}

export function isHdiActivity(activityType: string): boolean {
    return activityType === Metamodel.modelFor(SparkActivity).name
        || activityType === Metamodel.modelFor(HiveActivity).name
        || activityType === Metamodel.modelFor(MapReduceActivity).name
        || activityType === Metamodel.modelFor(PigActivity).name
        || activityType === Metamodel.modelFor(StreamingActivity).name;
}

function hasActivityRunDetails(activityRun: IActivityRun): boolean {
    return activityRun.activityType === Metamodel.modelFor(CopyActivity).name
        || isDatabricksActivity(activityRun.activityType)
        || isHdiActivity(activityRun.activityType)
        || activityRun.activityType === Metamodel.modelFor(AzureMLExecutePipelineActivity).name
        || activityRun.activityType === Metamodel.modelFor(TridentNotebookActivity).name
        || activityRun.activityType === Metamodel.modelFor(TridentSparkJobDefinitionActivity).name
        || (activityRun.activityType === Metamodel.modelFor(AzureFunctionActivity).name && showFunctionSetRunDetails(activityRun))
        || activityRun.activityType === Metamodel.modelFor(ExecutePipelineActivityDuplicate).name
        || activityRun.activityType === Metamodel.modelFor(InvokeCopyJobActivity).name
        || activityRun.activityType === Metamodel.modelFor(FabricApprovalActivity).name
        || activityRun.activityType === Metamodel.modelFor(InvokeDataBuildToolJobActivity).name;
}

function showActivityRunDetailsForFailedStatus(activityRun: IActivityRun): boolean {
    return activityRun.activityType === Metamodel.modelFor(CopyActivity).name
        || activityRun.activityType === Metamodel.modelFor(TridentNotebookActivity).name
        || activityRun.activityType === Metamodel.modelFor(TridentSparkJobDefinitionActivity).name
        || (activityRun.activityType === Metamodel.modelFor(AzureFunctionActivity).name && showFunctionSetRunDetails(activityRun))
        || activityRun.activityType === Metamodel.modelFor(ExecutePipelineActivityDuplicate).name
        || activityRun.activityType === Metamodel.modelFor(InvokeCopyJobActivity).name
        || activityRun.activityType === Metamodel.modelFor(InvokeDataBuildToolJobActivity).name;
}

function showFunctionSetRunDetails(activityRun: IActivityRun): boolean {
    const inputJson = JSON.parse(activityRun.input || '{}');
    return inputJson.functionSetId && inputJson.workspaceId && activityRun.status != stringResources.InProgress;
}

export function shouldShowRunDetailButton(activityRun: IActivityRun): boolean {
    if (activityRun.status === stringResources.Queued) {
        return false;
    }
    else if (__IS_FABRIC_DPE__ && isDataFlowActivity(activityRun.activityType)) {
        return true;
    }

    if (activityRun.status === stringResources.Failed && (activityRun.outputDetails == null || !showActivityRunDetailsForFailedStatus(activityRun))) {
        return false;
    }
    if (activityRun.status === stringResources.Canceled && activityRun.outputDetails == null) {
        return false;
    }
    return hasActivityRunDetails(activityRun);
}

export function shouldShowDataFlowDetailButton(activityRun: IActivityRun): boolean {
    const isQueued = activityRun.status === stringResources.Queued;
    const isWranglingDataFlow = isWranglingDataFlowActivity(activityRun.activityType);
    const isDataFlow = isDataFlowActivity(activityRun.activityType);
    return isWranglingDataFlow || (isDataFlow && !isQueued);
}

export function isDataFlowActivity(activityType: string): boolean {
    return stringsAreEqualCaseInsensitive(activityType, Metamodel.modelFor(ExecuteDataFlowActivity).name)
        || stringsAreEqualCaseInsensitive(activityType, 'executemappingdataflow'); // Dataflow runs through Azure IR return this value as activity type, adding this temporarily until this is fixed.
}

export function isWranglingDataFlowActivity(activityType: string): boolean {
    return stringsAreEqualCaseInsensitive(activityType, Metamodel.modelFor(ExecuteWranglingDataflowActivity).name);
}

export const activitiesWithFeedbackEntry: Set<string> = new Set<string>([
    Metamodel.modelFor(CopyActivity).name,
    Metamodel.modelFor(DeleteActivity).name,
    Metamodel.modelFor(GetMetadataActivity).name,
    Metamodel.modelFor(LookupActivity).name,
    Metamodel.modelFor(ExecuteSSISPackageActivity).name
]);

export function showActivityDocLinkEntry(activityType: string): boolean {
    return activitiesWithFeedbackEntry.has(activityType);
}

export function showActivityErrorFeedbackEntry(activityType: string): boolean {
    return activitiesWithFeedbackEntry.has(activityType);
}

export function isContainerActivity(activityConfig: IActivityConfiguration) {
    return (activityConfig instanceof ContainerActivityConfiguration
        || activityConfig instanceof SwitchActivityConfiguration
        || activityConfig instanceof IfConditionActivityConfiguration);
}

export function getMappingPipelineForContainerActivity(activity: Activity, activityConfig: IActivityConfiguration, caseValue: string): Pipeline {
    if (!isContainerActivity(activityConfig)) {
        return undefined;
    }
    if (activityConfig instanceof ContainerActivityConfiguration) {
        // ForEach or Until activity
        const pipeline = activityConfig.getMappingProperty(activity) as Pipeline;
        return pipeline;
    } else if (activityConfig instanceof SwitchActivityConfiguration) {
        // Switch activity
        let pipeline;
        const switchActivity = activity as SwitchActivity;
        if (caseValue === 'Default') {
            pipeline = switchActivity.defaultPipeline;
        } else {
            const selectedCase = switchActivity.cases.find((c) => {
                return c.value === caseValue;
            });
            pipeline = selectedCase.casePipeline;
        }

        return pipeline;

    } else if (activityConfig instanceof IfConditionActivityConfiguration) {
        const ifActivity = activity as IfConditionActivity;
        const pipeline = caseValue === 'True' ? activityConfig.getTrueMappingProperty(ifActivity) :
            activityConfig.getFalseMappingProperty(ifActivity);

        return pipeline;
    }

    return undefined;
}

export function activityCanBeAddedInNestedActivity(parentActivityModelName: string, childActivityConfig: IActivityConfiguration): boolean {
    // ForEach/Until activites are not allowed to be nested
    if (childActivityConfig instanceof ContainerActivityConfiguration) {
        return false;
    }

    // IfCondition/Switch only allowed in ForEach/Until activity.
    if ((childActivityConfig instanceof IfConditionActivityConfiguration || childActivityConfig instanceof SwitchActivityConfiguration)
        && parentActivityModelName !== Metamodel.modelFor(ForEachActivity).name
        && parentActivityModelName !== Metamodel.modelFor(UntilActivity).name) {
        return false;
    }
    // Validation only allowed in ForEach activity.
    if (childActivityConfig instanceof ValidationActivityConfiguration && parentActivityModelName !== Metamodel.modelFor(ForEachActivity).name) {
        return false;
    }
    return true;
}

export function getActivityType(activityName: string, pipeline: Pipeline): string {
    if (!pipeline) {
        return '';
    } else {

        const findActivity = (pipeline: Pipeline) => {
            return Array.from(pipeline.activities).find((act) => {
                return act.name === activityName;
            });
        };

        let activity = findActivity(pipeline);
        if (!activity && !!pipeline.owner) {
            let parentPipeline = pipeline.owner.owner as Pipeline;
            activity = findActivity(parentPipeline);
            if (!activity && !!parentPipeline.owner) {
                parentPipeline = parentPipeline.owner.owner as Pipeline;
                activity = findActivity(parentPipeline);
            }
        }

        return (activity) ? Metamodel.modelOf(activity).name : undefined;
    }
}

export function getIconClassForDynamicExpression(category: ExpressionCategory, activityType: string, activityRegistry: ActivityRegistryService): string {
    switch (category) {
        case ExpressionCategory.Parameter:
            return 'svg-i_fixed_parameterize_16x16';
        case ExpressionCategory.Variable:
            return 'svg-i_variable_16x16';
        case ExpressionCategory.SystemVariable:
            return 'svg-i_system_variable_16x16';
        case ExpressionCategory.IterableItem:
        case ExpressionCategory.ActivityOutput:
            return activityRegistry.getActivityIconClass(activityType);
        case ExpressionCategory.Function:
            // Note that for this we use a img instead of svg
            return 'function';
        default:
            return '';
    }
}

export function getBackgroundColorStyleClassForCategory(category: ExpressionCategory): string {
    switch (category) {
        case ExpressionCategory.Parameter:
            return 'input-token-parameter';
        case ExpressionCategory.Variable:
            return 'input-token-variable';
        case ExpressionCategory.IterableItem:
            return 'input-token-item';
        case ExpressionCategory.SystemVariable:
            return 'input-token-system-variable';
        case ExpressionCategory.ActivityOutput:
            return 'input-token-system-activity-output';
        case ExpressionCategory.Function:
            return 'input-token-function';
        default:
            return '';
    }
}

